import { DiskCollectionSortMode, setPreferenceDiskCollectionSort } from "../../localstorage"
import { handleGetSlotConfig, handleGetVeraSdStatus, handleSetVeraSdWriteProtected, passSetDriveProps } from "../../main2worker"
import { CLOUD_SYNC, getCleanDiskFilename, getDefaultDiskDriveIndex, getPrimaryDiskUrl } from "../../../common/utility"
import {
  DISK_COLLECTION_ITEM_TYPE,
  TAB_INDEX,
  createHdv,
  diskItemKey,
  diskCollectionSortOptions,
  formatBytes,
  getDefaultDiskCollectionSortMode,
  getDiskCollection,
  getDiskCollectionSortMode,
  getExportFilename,
  isDiskExportable,
  loadDisk,
  loadDiskIntoDrive,
  sortDisks,
} from "../../diskdialog/diskpanel_utils"
import { isFileSystemApiSupported, showGlobalProgressModal } from "../../ui_utilities"
import type { RetroControlMetadata, RetroMenuContext, RetroResolvedControl } from "../../retro/retromenucontext"
import { DiskBookmarks } from "./diskbookmarks"
import { newReleases } from "./newreleases"
import { createRetroDemoZooControl } from "./demozoo_retro"
import { createRetroInternetArchiveControl } from "./internetarchive_retro"
import {
  DISK_DRIVE_LABELS,
  downloadDiskToDevice,
  saveDiskToDevice,
} from "./diskdrive"
import { getDiskBlobForSync } from "../../../common/utility"
import {
  doSetUIDriveProps,
  ejectAllDisks,
  getStandbyActiveIndex,
  getStandbyDisks,
  handleEjectDisk,
  handleGetDriveProps,
  handleGetFilename,
  handleSaveWritableFile,
  handleSetDiskWriteProtected,
  swapStandbyDisk,
} from "./driveprops"
import { GoogleDrive } from "./googledrive"
import { createRetroGoogleDriveControl } from "./googledrive_retro"
import { createRetroOneDriveControl } from "./onedrive_retro"
import { OneDriveCloudDrive } from "./onedriveclouddrive"
import { canFavoriteDisk, isDiskFavorite, setDiskFavorite } from "./diskfavorites"
import {
  CLOUD_PROVIDER_NAMES,
  cloudProviderDisplayName,
  getCloudProvidersNeedingAuth,
  signInToCloudProvider,
} from "./cloudauth"
import "./diskinterface.css"
import DiskDrive from "./diskdrive"
import { DiskImageChooser } from "./diskimagechooser"
import { faHdd } from "@fortawesome/free-solid-svg-icons"
import { useEffect, useState } from "react"
import Flyout from "../../flyout"
import ImageWriter from "../printer/imagewriter"
import VeraSdIcon, {
  downloadAndEjectVeraSd,
  downloadVeraSd,
  ejectVeraSd,
  getStandbyVeraActiveIndex,
  getStandbyVeraDisks,
  isVeraSdSyncing,
  isVeraSdSyncPaused,
  loadVeraSdFromCloud,
  openSdFilePicker,
  saveVeraSdToCloud,
  saveVeraSdToDevice,
  swapVeraStandbyDisk,
  syncVeraSdNow,
  toggleVeraSdSyncPaused,
} from "../vera/verasdicon"
import { isMinimalTheme } from "../../ui_settings"
import { useTranslation } from "../../../i18n/useTranslation"
import { choiceBinding, controlFromJson, controlsFromJson, toggleBinding, type RetroControlBindings } from "../../retro/retrocontrolmetadata"

const decodeDiskTitle = (title: string) => {
  let decodedTitle = title
  try {
    decodedTitle = decodeURIComponent(title)
  } catch {
    // Keep malformed URL text as-is.
  }
  return decodedTitle.replace(/\u00A0/g, " ")
}

const getCollection = () => getDiskCollection(new DiskBookmarks(), newReleases)

let selectedCollectionDriveIndex: number | undefined
const revealedCollectionDriveTabs = new Set<TAB_INDEX>()

export const getSelectedCollectionDriveIndex = () => selectedCollectionDriveIndex

const setSelectedCollectionDriveIndex = (index: number | undefined) => {
  selectedCollectionDriveIndex = index
}

export const resetSelectedCollectionDriveIndex = () => {
  selectedCollectionDriveIndex = undefined
}

const resetCollectionDriveTab = (tabIndex: TAB_INDEX) => {
  selectedCollectionDriveIndex = undefined
  revealedCollectionDriveTabs.delete(tabIndex)
}

export const resetCollectionDriveSelectionSession = () => {
  selectedCollectionDriveIndex = undefined
  revealedCollectionDriveTabs.clear()
}

const getCollectionDriveIndex = (disk: DiskCollectionItem) => {
  if (selectedCollectionDriveIndex !== undefined) return selectedCollectionDriveIndex
  const filename = disk.cloudData?.fileName || getPrimaryDiskUrl(disk)
  const fileSize = disk.cloudData?.fileSize ?? disk.fileSize
  return getDefaultDiskDriveIndex(filename, fileSize)
}

const diskTemplateBindings: RetroControlBindings = {
  "diskDrives.blankSeparator": { label: (): string => "" },
  "diskDrives.cloudSection": { label: "Cloud" },
  "diskCollection.sort": { label: (): string => "" },
  "diskCollection.diskRow": { label: (): string => "" },
  "diskCollection.exportDiskRow": { label: (): string => "" },
  "diskCollection.cloudAuthNotification": { label: (): string => "" },
}

const cloudAuthNotificationControls = (
  tabIndex: TAB_INDEX,
  disks: DiskCollectionItem[],
): RetroControlMetadata[] => {
  const cloudDisks = disks.filter(disk => disk.type === DISK_COLLECTION_ITEM_TYPE.CLOUD_DRIVE)
  const providersNeedingAuth = getCloudProvidersNeedingAuth(cloudDisks)
  if (providersNeedingAuth.length === 0) return []
  const notifBase = controlFromJson("diskTemplates", "diskCollection.cloudAuthNotification", diskTemplateBindings)
  const sepBase = controlFromJson("diskTemplates", "diskCollection.cloudAuthNotificationSeparator", {})
  return [
    ...CLOUD_PROVIDER_NAMES.filter(providerName => providersNeedingAuth.includes(providerName)).map(
      (providerName): RetroControlMetadata => ({
        ...notifBase,
        id: `diskCollection.${tabIndex}.notification.${providerName}`,
        label: context => `*${cloudProviderDisplayName(providerName)} ${context.t("collection.cloudAuthRequired")}`,
        action: async context => {
          const authReady = await signInToCloudProvider(providerName)
          if (authReady) {
            context.notifyCloudAuthChanged?.()
            context.displayProps.updateDisplay()
          }
        },
      }),
    ),
    {
      ...sepBase,
      id: `diskCollection.${tabIndex}.notificationsSeparator`,
    },
  ]
}

const exportDisks = async (context: RetroMenuContext, disks: DiskCollectionItem[]) => {
  context.close()
  const downloadedDisks: DownloadedExportDisk[] = []
  try {
    for (let index = 0; index < disks.length; index += 1) {
      const disk = disks[index]
      showGlobalProgressModal(true, context.t("retroControl.fetchingDisk", {
        current: String(index + 1),
        total: String(disks.length),
      }))
      const buffer = await new Promise<ArrayBuffer>((resolve, reject) => {
        loadDisk(-1, disk, context.displayProps.updateDisplay, result => {
          if (result) resolve(result)
          else reject(new Error(context.t("retroControl.downloadFailed", {
            title: decodeDiskTitle(disk.title),
          })))
        })
      })
      const data = new Uint8Array(buffer)
      downloadedDisks.push({ item: disk, buffer: data, filename: getExportFilename(disk, data) })
    }
    await createHdv(downloadedDisks)
  } catch (error) {
    showGlobalProgressModal(false)
    const message = error instanceof Error ? error.message : String(error)
    alert(context.t("retroControl.exportFailed", { message }))
  }
}

export const createRetroExportItems = (
  _context: RetroMenuContext,
  disks: DiskCollectionItem[],
  sortMode: DiskCollectionSortMode = getDiskCollectionSortMode(TAB_INDEX.EXPORT),
  selectedDiskKeys = new Set<string>(),
): RetroControlMetadata[] => {
  const exportableDisks = disks.filter(isDiskExportable)
  if (exportableDisks.length === 0) return []
  const sortIndex = diskCollectionSortOptions.findIndex(option => option.value === sortMode)
  const defaultSortIndex = diskCollectionSortOptions.findIndex(
    option => option.value === getDefaultDiskCollectionSortMode(TAB_INDEX.EXPORT),
  )
  const sortControl: RetroControlMetadata = {
    ...controlFromJson("diskTemplates", "diskCollection.{{tabIndex}}.sort", diskTemplateBindings, { tabIndex: "export" }),
    ...choiceBinding({
      options: () => diskCollectionSortOptions.map(option => ({ label: option.label })),
      currentIndex: () => sortIndex,
      select: (_runtime, index) => {
        setPreferenceDiskCollectionSort(TAB_INDEX.EXPORT, diskCollectionSortOptions[index].value)
      },
    }),
    defaultIndex: defaultSortIndex,
  }
  sortControl.refreshOptions = (runtime, index, items, values) => createRetroExportScreenItems(
    runtime,
    disks,
    items,
    values,
    diskCollectionSortOptions[index].value,
  )
  const exportDiskRowBase = controlFromJson("diskTemplates", "diskCollection.exportDiskRow", diskTemplateBindings)
  const sortSepBase = controlFromJson("diskTemplates", "diskCollection.{{tabIndex}}.sortSeparator", {}, { tabIndex: "export" })
  return [
    ...sortDisks(exportableDisks, sortMode).map((disk): RetroControlMetadata => ({
      ...exportDiskRowBase,
      id: `diskCollection.export.disk.${encodeURIComponent(diskItemKey(disk))}`,
      label: decodeDiskTitle(disk.title),
      options: [{ label: "" }, { label: "" }],
      optionIndex: selectedDiskKeys.has(diskItemKey(disk)) ? 1 : 0,
      payload: disk,
      indicator: disk.vtocType === undefined ? "?" : undefined,
      selectable: disk.vtocType !== undefined,
      bulkSelectable: disk.vtocType !== undefined,
      refreshOptions: (runtime, _index, items, values) => createRetroExportScreenItems(
        runtime,
        disks,
        items,
        values,
        sortMode,
      ),
    })),
    sortSepBase,
    sortControl,
  ]
}

export const getRetroVtocIndicator = (
  disk: DiskCollectionItem,
  activeVtocCheckKey: string | null,
  spinner: string,
) => disk.vtocType === undefined
    ? (diskItemKey(disk) === activeVtocCheckKey ? spinner : "?")
    : undefined

const selectedExportDiskKeys = (
  items: readonly RetroResolvedControl[] = [],
  values: number[] = [],
) => new Set(items.flatMap((item, index) =>
  values[index] === item.checkmarkIndex && item.payload
    ? [diskItemKey(item.payload as DiskCollectionItem)]
    : []))

const maxHdvBytes = 33554432

const selectedExportSize = (
  items: readonly RetroResolvedControl[] = [],
  values: number[] = [],
) => items.reduce((total, item, index) => {
  if (values[index] !== item.checkmarkIndex || !item.payload) return total
  const disk = item.payload as DiskCollectionItem
  if (disk.cloudData) return total + (disk.cloudData.fileSize > 0 ? disk.cloudData.fileSize : 143360)
  return total + Math.max(0, disk.fileSize)
}, 0)

export const getRetroExportHdvSize = (
  items: readonly RetroResolvedControl[] = [],
  values: number[] = [],
) => {
  const selectedBytes = selectedExportSize(items, values)
  return selectedBytes > 0
    ? `${formatBytes(selectedBytes)} / ${formatBytes(maxHdvBytes)}`
    : undefined
}

const exportNotificationDisks = (
  disks: DiskCollectionItem[],
  selectedDiskKeys: ReadonlySet<string>,
) => disks.filter(disk => disk.vtocType === undefined || selectedDiskKeys.has(diskItemKey(disk)))

export const createRetroExportScreenItems = (
  context: RetroMenuContext,
  disks: DiskCollectionItem[],
  items?: readonly RetroResolvedControl[],
  values?: number[],
  sortMode = getDiskCollectionSortMode(TAB_INDEX.EXPORT),
) => {
  const selectedDiskKeys = selectedExportDiskKeys(items, values)
  return [
    ...cloudAuthNotificationControls(
      TAB_INDEX.EXPORT,
      exportNotificationDisks(disks, selectedDiskKeys),
    ),
    ...createRetroExportItems(context, disks, sortMode, selectedDiskKeys),
  ]
}

const collectionItems = (
  context: RetroMenuContext,
  tabIndex: TAB_INDEX,
  disks: DiskCollectionItem[],
  sortMode: DiskCollectionSortMode = getDiskCollectionSortMode(tabIndex),
  items?: readonly RetroResolvedControl[],
  values?: number[],
): RetroControlMetadata[] => {
  if (disks.length === 0) return []
  const sortIndex = diskCollectionSortOptions.findIndex(option => option.value === sortMode)
  const defaultSortIndex = diskCollectionSortOptions.findIndex(
    option => option.value === getDefaultDiskCollectionSortMode(tabIndex),
  )
  const sortControl: RetroControlMetadata = {
    ...controlFromJson("diskTemplates", "diskCollection.{{tabIndex}}.sort", diskTemplateBindings, { tabIndex }),
    ...choiceBinding({
      options: () => diskCollectionSortOptions.map(option => ({ label: option.label })),
      currentIndex: () => sortIndex,
      select: (_runtime, index) => {
        setPreferenceDiskCollectionSort(tabIndex, diskCollectionSortOptions[index].value)
      },
    }),
    defaultIndex: defaultSortIndex,
  }
  sortControl.refreshOptions = (runtime, index, currentItems, currentValues) => collectionItems(
    runtime,
    tabIndex,
    disks,
    diskCollectionSortOptions[index].value,
    currentItems,
    currentValues,
  )
  const driveOptions = DISK_DRIVE_LABELS.map(label => ({ label }))
  const isFavorites = tabIndex === TAB_INDEX.FAVORITES
  const markedFavoriteKeys = new Set(items?.flatMap((item, index) =>
    values?.[index] === item.checkmarkIndex && item.payload
      ? [diskItemKey(item.payload as DiskCollectionItem)]
      : []))
  const diskRowBase = controlFromJson("diskTemplates", "diskCollection.diskRow", diskTemplateBindings)
  const sortSepBase = controlFromJson("diskTemplates", "diskCollection.{{tabIndex}}.sortSeparator", {}, { tabIndex })
  return [
    ...sortDisks(disks, sortMode).map((disk, index): RetroControlMetadata => ({
      ...diskRowBase,
      id: `diskCollection.${tabIndex}.disk.${index}`,
      label: decodeDiskTitle(disk.title),
      payload: disk,
      options: isFavorites ? [{ label: "" }, { label: "" }] : driveOptions,
      optionIndex: () => isFavorites
        ? markedFavoriteKeys.has(diskItemKey(disk)) ? 1 : 0
        : getCollectionDriveIndex(disk),
      checkmarkIndex: isFavorites ? 1 : undefined,
      checkedIndicator: isFavorites ? "X" : undefined,
      revealOptionOnFirstHorizontalInput: !isFavorites,
      contextualSubmenuTitleValue: () => isFavorites || !revealedCollectionDriveTabs.has(tabIndex)
        ? undefined
        : DISK_DRIVE_LABELS[getCollectionDriveIndex(disk)],
      refreshOptions: isFavorites ? undefined : (runtime, driveIndex) => {
        setSelectedCollectionDriveIndex(driveIndex)
        revealedCollectionDriveTabs.add(tabIndex)
        return collectionItems(runtime, tabIndex, disks, sortMode)
      },
      action: runtime => {
        if (selectedCollectionDriveIndex !== undefined) {
          loadDiskIntoDrive(
            getCollectionDriveIndex(disk),
            disk,
            runtime.displayProps.updateDisplay,
            runtime.close,
          )
        } else {
          loadDisk(
            -1,
            disk,
            runtime.displayProps.updateDisplay,
            undefined,
            runtime.close,
          )
        }
      },
    })),
    sortSepBase,
    sortControl,
  ]
}

const collectionTabs = [
  {
    id: "diskCollection.builtIn",
    index: TAB_INDEX.BUILT_IN,
    filter: (disk: DiskCollectionItem) => disk.type === DISK_COLLECTION_ITEM_TYPE.A2TS_ARCHIVE,
  },
  {
    id: "diskCollection.newReleases",
    index: TAB_INDEX.NEW_RELEASES,
    filter: (disk: DiskCollectionItem) => disk.type === DISK_COLLECTION_ITEM_TYPE.NEW_RELEASE,
  },
  {
    id: "diskCollection.favorites",
    index: TAB_INDEX.FAVORITES,
    filter: (disk: DiskCollectionItem) =>
      disk.type === DISK_COLLECTION_ITEM_TYPE.INTERNET_ARCHIVE ||
      disk.type === DISK_COLLECTION_ITEM_TYPE.CLOUD_DRIVE ||
      disk.type === DISK_COLLECTION_ITEM_TYPE.DEMOZOO,
  },
  {
    id: "diskCollection.export",
    index: TAB_INDEX.EXPORT,
    filter: isDiskExportable,
  },
] as const

const diskLoadItems = (driveIndex: number): RetroControlMetadata[] => [
  controlFromJson(
    "diskTemplates",
    "diskDrives.{{driveIndex}}.load.device",
    {
      "diskDrives.load.device": {
        action: context => {
          context.displayProps.setShowFileOpenDialog(true, driveIndex)
        },
      },
    },
    { driveIndex },
  ),
  {
    ...controlFromJson(
      "diskTemplates",
      "diskDrives.{{driveIndex}}.loadAdditional",
      {},
      { driveIndex },
    ),
    selectable: () => Boolean(handleGetDriveProps(driveIndex).filename),
    action: context => {
      context.displayProps.setShowFileOpenDialog(true, driveIndex, true)
    },
  },
  {
    ...controlFromJson(
      "diskTemplates",
      "diskDrives.{{driveIndex}}.load.from",
      {},
      { driveIndex },
    ),
    dynamicChildren: () => [
      createRetroInternetArchiveControl(driveIndex),
      createRetroDemoZooControl(driveIndex),
      createRetroOneDriveControl(driveIndex),
      createRetroGoogleDriveControl(driveIndex),
    ],
  },
]

const diskMenuSeparator = (driveIndex: number | string, id: string): RetroControlMetadata =>
  controlFromJson("diskTemplates", "diskDrives.{{driveIndex}}.{{separatorId}}", diskTemplateBindings, { driveIndex, separatorId: id })

const diskMenuGroup = (
  driveIndex: number | string,
  id: string,
  label: string | ((context: RetroMenuContext) => string),
  items: RetroControlMetadata[],
): RetroControlMetadata[] => items.length > 0
    ? [{ ...diskMenuSeparator(driveIndex, id), label }, ...items]
    : []

const veraSdLoadItems = (): RetroControlMetadata[] => [
  controlFromJson(
    "diskTemplates",
    "diskDrives.{{driveIndex}}.load.device",
    {
      "diskDrives.load.device": {
        action: () => {
          void openSdFilePicker()
        },
      },
    },
    { driveIndex: "vera" },
  ),
  {
    ...controlFromJson(
      "diskTemplates",
      "diskDrives.{{driveIndex}}.loadAdditional",
      {},
      { driveIndex: "vera" },
    ),
    selectable: () => Boolean(handleGetVeraSdStatus().attached),
    action: () => {
      void openSdFilePicker(true)
    },
  },
  {
    ...controlFromJson(
      "diskTemplates",
      "diskDrives.{{driveIndex}}.load.from",
      {},
      { driveIndex: "vera" },
    ),
    dynamicChildren: () => [
      {
        ...controlFromJson("diskTemplates", "diskDrives.{{driveIndex}}.load.oneDrive", {}, { driveIndex: "vera" }),
        action: () => { void loadVeraSdFromCloud(new OneDriveCloudDrive()) },
      },
      {
        ...controlFromJson("diskTemplates", "diskDrives.{{driveIndex}}.load.googleDrive", {}, { driveIndex: "vera" }),
        action: () => { void loadVeraSdFromCloud(new GoogleDrive()) },
      },
    ],
  },
]

const veraSdInsertedItems = (): RetroControlMetadata[] => {
  const status = handleGetVeraSdStatus()
  const hasDisk = Boolean(status.attached)
  const isElectron = typeof navigator !== "undefined" && navigator.userAgent.includes("Electron")

  const diskItems: RetroControlMetadata[] = [
    {
      ...controlFromJson("diskTemplates", "diskDrives.{{driveIndex}}.writeProtected", {}, { driveIndex: "vera" }),
      selectable: () => hasDisk,
      ...toggleBinding({
        enabled: () => Boolean(handleGetVeraSdStatus().writeProtected),
        setEnabled: (context, enabled) => {
          handleSetVeraSdWriteProtected(enabled)
          context.displayProps.updateDisplay()
        },
      }),
    },
  ]

  const downloadItems: RetroControlMetadata[] = [
    {
      ...controlFromJson("diskTemplates", "diskDrives.{{driveIndex}}.download", {}, { driveIndex: "vera" }),
      selectable: () => hasDisk,
      action: () => { void downloadVeraSd() },
    },
    {
      ...controlFromJson("diskTemplates", "diskDrives.{{driveIndex}}.downloadAndEject", {}, { driveIndex: "vera" }),
      selectable: () => hasDisk,
      action: (currentContext: RetroMenuContext) => {
        void downloadAndEjectVeraSd()
        currentContext.displayProps.updateDisplay()
      },
    },
    {
      ...controlFromJson("diskTemplates", "diskDrives.{{driveIndex}}.eject", {}, { driveIndex: "vera" }),
      selectable: () => hasDisk,
      action: (currentContext: RetroMenuContext) => {
        ejectVeraSd()
        currentContext.displayProps.updateDisplay()
      },
    },
  ]

  const saveToItems: RetroControlMetadata[] = [
    {
      ...controlFromJson("diskTemplates", "diskDrives.{{driveIndex}}.saveToOneDrive", {}, { driveIndex: "vera" }),
      selectable: () => hasDisk && !isElectron,
      action: () => { void saveVeraSdToCloud(new OneDriveCloudDrive()) },
    },
    {
      ...controlFromJson("diskTemplates", "diskDrives.{{driveIndex}}.saveToGoogleDrive", {}, { driveIndex: "vera" }),
      selectable: () => hasDisk && !isElectron,
      action: () => { void saveVeraSdToCloud(new GoogleDrive()) },
    },
  ]

  const saveItems: RetroControlMetadata[] = [
    {
      ...controlFromJson("diskTemplates", "diskDrives.{{driveIndex}}.saveToDevice", {}, { driveIndex: "vera" }),
      selectable: () => hasDisk && isFileSystemApiSupported(),
      action: () => { void saveVeraSdToDevice() },
    },
    {
      ...controlFromJson("diskTemplates", "diskDrives.{{driveIndex}}.saveTo", {}, { driveIndex: "vera" }),
      selectable: () => hasDisk && !isElectron,
      dynamicChildren: () => saveToItems,
    },
    {
      ...controlFromJson("diskTemplates", "diskDrives.{{driveIndex}}.pauseSyncing", {}, { driveIndex: "vera" }),
      selectable: () => hasDisk,
      indicator: () => isVeraSdSyncPaused() ? "*" : undefined,
      action: (currentContext: RetroMenuContext) => {
        toggleVeraSdSyncPaused()
        currentContext.displayProps.updateDisplay()
      },
    },
    {
      ...controlFromJson("diskTemplates", "diskDrives.{{driveIndex}}.syncNow", {}, { driveIndex: "vera" }),
      selectable: () => hasDisk && !isVeraSdSyncing(),
      action: () => { void syncVeraSdNow() },
    },
  ]

  // Multi-disk swap group — only shown when there are 2 or more valid standby disks
  const standby = getStandbyVeraDisks()
  const validDisks = standby.filter(d => Boolean(d?.filename?.trim()))

  const swapGroup: RetroControlMetadata[] = validDisks.length >= 2 ? diskMenuGroup(
    "vera",
    "swapSeparator",
    (context: RetroMenuContext) => context.t("disk.standbyDisks"),
    [
      ...validDisks.map((disk, idx) => {
        const cleanName = getCleanDiskFilename(disk.filename)
        const displayName = disk.comment ? `${cleanName} (${disk.comment})` : cleanName
        return {
          ...controlFromJson(
            "diskTemplates",
            "diskDrives.{{driveIndex}}.swapDisk.{{diskIndex}}",
            diskTemplateBindings,
            { driveIndex: "vera", diskIndex: idx },
          ),
          label: displayName,
          indicator: () => idx === getStandbyVeraActiveIndex() ? "*" : undefined,
          selectable: () => true,
          refreshAfterAction: true,
          action: async (ctx?: RetroMenuContext) => {
            if (idx !== getStandbyVeraActiveIndex()) {
              await swapVeraStandbyDisk(idx)
              ctx?.displayProps?.updateDisplay?.()
            }
          },
        }
      }),
      {
        ...controlFromJson(
          "diskTemplates",
          "diskDrives.{{driveIndex}}.ejectAll",
          {},
          { driveIndex: "vera" },
        ),
        selectable: () => true,
        action: (ctx: RetroMenuContext) => {
          ejectVeraSd()
          ctx.displayProps.updateDisplay()
        },
      },
    ],
  ) : []

  return [
    ...swapGroup,
    ...diskMenuGroup("vera", "diskSeparator", "Disk", diskItems),
    ...diskMenuGroup("vera", "downloadSeparator", "Download", downloadItems),
    ...diskMenuGroup("vera", "saveSeparator", "Save", saveItems),
  ]
}

const veraSdRetroItems = (): RetroControlMetadata[] => [
  ...veraSdLoadItems(),
  ...veraSdInsertedItems(),
]

const getCurrentDiskScreenshotUrl = () => {
  const canvas = document.getElementById("hiddenCanvas") as HTMLCanvasElement | null
  return new URL(canvas?.toDataURL("image/jpeg", 0.1) ?? "data:image/jpeg,")
}

const saveDiskToCloud = async (driveIndex: number, provider: CloudProvider) => {
  const drive = handleGetDriveProps(driveIndex)
  const blob = getDiskBlobForSync(drive.diskData, drive.filename, drive.hardDrive)
  const cloudData = await provider.upload(
    drive.filename,
    blob,
  )
  if (!cloudData) return
  if (drive.writableFileHandle) await handleSaveWritableFile(driveIndex)
  passSetDriveProps({
    ...drive,
    cloudData,
    writableFileHandle: null,
    diskHasChanges: false,
  })
}

const syncCloudDisk = async (driveIndex: number) => {
  const drive = handleGetDriveProps(driveIndex)
  if (!drive.cloudData || drive.cloudData.isArchive) return
  const provider = drive.cloudData.providerName === "OneDrive"
    ? new OneDriveCloudDrive()
    : drive.cloudData.providerName === "GoogleDrive"
      ? new GoogleDrive()
      : null
  if (!provider) return
  const targetFilename = drive.cloudData.fileName || drive.filename
  const blob = getDiskBlobForSync(drive.diskData, targetFilename, drive.hardDrive)
  if (!await provider.sync(blob, drive.cloudData)) return
  passSetDriveProps({
    ...drive,
    cloudData: { ...drive.cloudData, fileSize: blob.size },
    diskHasChanges: false,
  })
}

const favoriteItem = (
  driveIndex: number,
  drive: DriveProps,
): RetroControlMetadata => {
  return {
    ...controlFromJson("diskTemplates", "diskDrives.{{driveIndex}}.favorite", {}, { driveIndex }),
    selectable: () => canFavoriteDisk(drive),
    ...toggleBinding({
      enabled: currentContext => Boolean(
        currentContext.diskBookmarks && isDiskFavorite(currentContext.diskBookmarks, drive)
      ),
      setEnabled: (currentContext, enabled) => {
        if (!currentContext.diskBookmarks) return
        setDiskFavorite(
          currentContext.diskBookmarks,
          drive,
          enabled,
          currentContext.getDiskScreenshotUrl?.() ?? getCurrentDiskScreenshotUrl(),
        )
      },
    }),
  }
}

const baseWriteProtectItem = (driveIndex: number, drive: DriveProps): RetroControlMetadata => ({
  ...controlFromJson("diskTemplates", "diskDrives.{{driveIndex}}.writeProtected", {}, { driveIndex }),
  selectable: () => Boolean(drive.filename),
  ...toggleBinding({
    enabled: () => drive.isWriteProtected,
    setEnabled: (context, enabled) => {
      handleSetDiskWriteProtected(driveIndex, enabled)
      context.displayProps.updateDisplay()
    },
  }),
})

const ejectItem = (driveIndex: number): RetroControlMetadata => ({
  ...controlFromJson("diskTemplates", "diskDrives.{{driveIndex}}.eject", {}, { driveIndex }),
  selectable: () => Boolean(handleGetDriveProps(driveIndex).filename),
  action: context => {
    handleEjectDisk(driveIndex)
    context.displayProps.updateDisplay()
  },
})

export const insertedDiskItems = (
  driveIndex: number,
): RetroControlMetadata[] => {
  const drive = handleGetDriveProps(driveIndex)
  const hasDisk = Boolean(drive.filename)
  const isElectron = navigator.userAgent.includes("Electron")
  const activeCloudDisk = Boolean(
    drive.cloudData &&
    drive.cloudData.syncStatus !== CLOUD_SYNC.INACTIVE &&
    !drive.cloudData.isArchive
  )
  const supportedCloudDisk = Boolean(
    activeCloudDisk && (
      drive.cloudData?.providerName === "OneDrive" || drive.cloudData?.providerName === "GoogleDrive"
    )
  )
  const setSyncInterval = (syncInterval: number) => {
    const latestDrive = handleGetDriveProps(driveIndex)
    if (!latestDrive.cloudData) return
    doSetUIDriveProps({
      ...latestDrive,
      cloudData: { ...latestDrive.cloudData, syncInterval },
    })
  }

  const downloadItems: RetroControlMetadata[] = [
    {
      ...controlFromJson("diskTemplates", "diskDrives.{{driveIndex}}.download", {}, { driveIndex }),
      selectable: () => hasDisk,
      action: () => downloadDiskToDevice(driveIndex),
    },
    {
      ...controlFromJson("diskTemplates", "diskDrives.{{driveIndex}}.downloadWoz", {}, { driveIndex }),
      selectable: () => hasDisk && !drive.hardDrive && !drive.filename.toLowerCase().endsWith(".woz"),
      action: () => downloadDiskToDevice(driveIndex, true),
    },
    {
      ...controlFromJson("diskTemplates", "diskDrives.{{driveIndex}}.downloadAndEject", {}, { driveIndex }),
      selectable: () => hasDisk,
      action: (currentContext: RetroMenuContext) => {
        downloadDiskToDevice(driveIndex)
        handleEjectDisk(driveIndex)
        currentContext.displayProps.updateDisplay()
      },
    },
    ejectItem(driveIndex),
  ]

  const saveToItems: RetroControlMetadata[] = [
    {
      ...controlFromJson("diskTemplates", "diskDrives.{{driveIndex}}.saveToOneDrive", {}, { driveIndex }),
      selectable: () => hasDisk && !isElectron,
      action: () => { void saveDiskToCloud(driveIndex, new OneDriveCloudDrive()) },
    },
    {
      ...controlFromJson("diskTemplates", "diskDrives.{{driveIndex}}.saveToGoogleDrive", {}, { driveIndex }),
      selectable: () => hasDisk && !isElectron,
      action: () => { void saveDiskToCloud(driveIndex, new GoogleDrive()) },
    },
  ]

  const saveItems: RetroControlMetadata[] = [
    {
      ...controlFromJson("diskTemplates", "diskDrives.{{driveIndex}}.saveToDevice", {}, { driveIndex }),
      selectable: () => hasDisk && isFileSystemApiSupported() && !drive.writableFileHandle,
      action: () => { void saveDiskToDevice(driveIndex) },
    },
    {
      ...controlFromJson("diskTemplates", "diskDrives.{{driveIndex}}.saveTo", {}, { driveIndex }),
      selectable: () => hasDisk && !isElectron,
      dynamicChildren: () => saveToItems,
    },
    {
      ...controlFromJson("diskTemplates", "diskDrives.{{driveIndex}}.pauseSyncing", {}, { driveIndex }),
      selectable: () => activeCloudDisk,
      indicator: () => drive.cloudData?.syncInterval === Number.MAX_VALUE ? "*" : undefined,
      action: () => setSyncInterval(Number.MAX_VALUE),
    },
    {
      ...controlFromJson("diskTemplates", "diskDrives.{{driveIndex}}.syncNow", {}, { driveIndex }),
      selectable: () => supportedCloudDisk,
      action: () => { void syncCloudDisk(driveIndex) },
    },
  ]

  const favorite = favoriteItem(
    driveIndex,
    drive,
  )

  // Multi-disk swap group — only shown when there are 2 or more valid standby disks
  const standby = getStandbyDisks(driveIndex)
  const validDisks = standby.filter(d => Boolean(d?.filename?.trim()))

  const swapGroup: RetroControlMetadata[] = validDisks.length >= 2 ? diskMenuGroup(
    driveIndex,
    "swapSeparator",
    (context: RetroMenuContext) => context.t("disk.standbyDisks"),
    [
      ...validDisks.map((disk, idx) => {
        const cleanName = getCleanDiskFilename(disk.filename)
        const displayName = disk.comment ? `${cleanName} (${disk.comment})` : cleanName
        return {
          ...controlFromJson(
            "diskTemplates",
            "diskDrives.{{driveIndex}}.swapDisk.{{diskIndex}}",
            diskTemplateBindings,
            { driveIndex, diskIndex: idx },
          ),
          label: displayName,
          indicator: () => idx === getStandbyActiveIndex(driveIndex) ? "*" : undefined,
          selectable: () => true,
          refreshAfterAction: true,
          action: async (ctx?: RetroMenuContext) => {
            if (idx !== getStandbyActiveIndex(driveIndex)) {
              const curDrive = handleGetDriveProps(driveIndex)
              if (curDrive.diskHasChanges && curDrive.writableFileHandle) {
                await handleSaveWritableFile(driveIndex)
              }
              await swapStandbyDisk(driveIndex, idx, undefined, () => ctx?.displayProps?.updateDisplay?.())
            }
          },
        }
      }),
      {
        ...controlFromJson(
          "diskTemplates",
          "diskDrives.{{driveIndex}}.ejectAll",
          {},
          { driveIndex },
        ),
        selectable: () => true,
        action: (ctx: RetroMenuContext) => {
          ejectAllDisks(driveIndex, () => ctx.displayProps.updateDisplay())
        },
      },
    ],
  ) : []

  return [
    ...swapGroup,
    ...diskMenuGroup(driveIndex, "diskSeparator", "Disk", [
      baseWriteProtectItem(driveIndex, drive),
      favorite,
    ]),
    ...diskMenuGroup(driveIndex, "downloadSeparator", "Download", downloadItems),
    ...diskMenuGroup(driveIndex, "saveSeparator", "Save", saveItems),
  ]
}

const diskDrives = [
  { index: 0, slot: 7 },
  { index: 1, slot: 7 },
  { index: 2, slot: 6 },
  { index: 3, slot: 6 },
] as const

const diskBindings: RetroControlBindings = {
  ...Object.fromEntries(collectionTabs.map(tab => [
    tab.id,
    {
      submenuTitleValue: tab.index === TAB_INDEX.EXPORT
        ? (_runtime: RetroMenuContext, items: readonly RetroResolvedControl[] = [], values: number[] = []) =>
          getRetroExportHdvSize(items, values)
        : undefined,
      dynamicChildren: (runtime: RetroMenuContext, items?: readonly RetroResolvedControl[], values?: number[]) => {
        const disks = (tab.index === TAB_INDEX.FAVORITES || tab.index === TAB_INDEX.EXPORT
          ? getCollection()
          : runtime.diskCollection ?? getCollection()).filter(tab.filter)
        if (tab.index !== TAB_INDEX.EXPORT && items === undefined && values === undefined) {
          resetCollectionDriveTab(tab.index)
        }
        return tab.index === TAB_INDEX.EXPORT
          ? createRetroExportScreenItems(runtime, disks, items, values)
          : collectionItems(runtime, tab.index, disks, getDiskCollectionSortMode(tab.index), items, values)
      },
      actionLabel: (runtime: RetroMenuContext) => runtime.t(tab.index === TAB_INDEX.EXPORT ? "collection.export" : "retroControl.load"),
      submit: tab.index === TAB_INDEX.EXPORT
        ? (runtime: RetroMenuContext, items: readonly RetroResolvedControl[], values: number[]) => {
          const selectedDisks = items
            .filter((item, index) => values[index] === 1 && item.payload)
            .map(item => item.payload as DiskCollectionItem)
          void exportDisks(runtime, selectedDisks)
        }
        : undefined,
      onLeave: tab.index === TAB_INDEX.FAVORITES
        ? (runtime: RetroMenuContext, items: readonly RetroResolvedControl[], values: number[]) => {
          items.forEach((item, index) => {
            const bookmarkId = (item.payload as DiskCollectionItem | undefined)?.bookmarkId
            if (bookmarkId && values[index] === item.checkmarkIndex) {
              runtime.diskBookmarks?.remove(bookmarkId)
            }
          })
        }
        : undefined,
      isSubmitVisible: tab.index === TAB_INDEX.EXPORT
        ? (_runtime: RetroMenuContext, items: readonly RetroResolvedControl[], values: number[]) => {
          const selectedDiskKeys = selectedExportDiskKeys(items, values)
          const disks = items.flatMap(item => item.payload ? [item.payload as DiskCollectionItem] : [])
          return selectedDiskKeys.size > 0 && getCloudProvidersNeedingAuth(
            exportNotificationDisks(disks, selectedDiskKeys)
              .filter(disk => disk.type === DISK_COLLECTION_ITEM_TYPE.CLOUD_DRIVE),
          ).length === 0
        }
        : undefined,
    } satisfies Partial<RetroControlMetadata>,
  ])),
  "diskDrives.none": {
    isVisible: () => diskDrives.every(({ slot }) => handleGetSlotConfig()[slot] === "none")
      && handleGetSlotConfig()[2] !== "vera" && handleGetSlotConfig()[4] !== "vera",
  },
  "diskDrives.vera": {
    label: (context: RetroMenuContext) => {
      const cfg = handleGetSlotConfig()
      const slot = cfg[2] === "vera" ? 2 : (cfg[4] === "vera" ? 4 : 2)
      const status = handleGetVeraSdStatus()
      return context.t("retroControl.drive", {
        drive: `S${slot},SD`,
        disk: status.attached
          ? `${status.hasChanges ? "*" : ""}${decodeDiskTitle(status.name)}`
          : context.t("retroControl.card.empty"),
      })
    },
    isVisible: () => {
      const cfg = handleGetSlotConfig()
      return cfg[2] === "vera" || cfg[4] === "vera"
    },
    dynamicChildren: () => veraSdRetroItems(),
    actionLabel: (context: RetroMenuContext) => context.t(handleGetVeraSdStatus().attached
      ? "retroControl.select"
      : "retroControl.load"),
    contextualActionLabel: (context: RetroMenuContext) => handleGetVeraSdStatus().attached
      ? context.t("retroControl.options")
      : context.t("retroControl.load"),
  },
  ...Object.fromEntries(diskDrives.map(({ index, slot }) => [
    `diskDrives.${index}`,
    {
      label: (context: RetroMenuContext) => {
        const drive = handleGetDriveProps(index)
        const filename = handleGetFilename(index)
        return context.t("retroControl.drive", {
          drive: DISK_DRIVE_LABELS[index],
          disk: filename
            ? `${drive.diskHasChanges ? "*" : ""}${decodeDiskTitle(filename)}`
            : context.t("retroControl.card.empty"),
        })
      },
      isVisible: () => handleGetSlotConfig()[slot] !== "none",
      dynamicChildren: () => [
        ...diskLoadItems(index),
        ...insertedDiskItems(index),
      ],
      actionLabel: (context: RetroMenuContext) => context.t(handleGetDriveProps(index).filename
        ? "retroControl.select"
        : "retroControl.load"),
      contextualActionLabel: (context: RetroMenuContext) => handleGetDriveProps(index).filename
        ? context.t("retroControl.options")
        : context.t("retroControl.load"),
    } satisfies Partial<RetroControlMetadata>,
  ])),
}

export const retroDiskControls: RetroControlMetadata[] = controlsFromJson("disk", diskBindings)

const DiskInterface = (props: DisplayProps) => {
  const { t } = useTranslation()
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false)
  const height = window.innerHeight ? window.innerHeight : (window.outerHeight - 120)
  const width = window.innerWidth ? window.innerWidth : (window.outerWidth - 20)
  const isScreenNarrow = width < height

  const [slotConfig, setSlotConfig] = useState(handleGetSlotConfig)
  useEffect(() => {
    const handleSlotChange = () => setSlotConfig({ ...handleGetSlotConfig() })
    window.addEventListener("apple2ts-slot-config-changed", handleSlotChange)
    return () => window.removeEventListener("apple2ts-slot-config-changed", handleSlotChange)
  }, [])

  const isVeraInstalled = slotConfig[2] === "vera" || slotConfig[4] === "vera"
  const allSlotsDisabled = slotConfig[1] === "none" && slotConfig[6] === "none" && slotConfig[7] === "none" && !isVeraInstalled

  return (
    <span style={{ opacity: allSlotsDisabled ? 0.4 : 1, filter: allSlotsDisabled ? "grayscale(100%)" : "none", pointerEvents: allSlotsDisabled ? "none" : "auto", cursor: allSlotsDisabled ? "not-allowed" : "pointer" }}>
      <Flyout
        icon={faHdd}
        title={t("disk.diskDrivesAndDevices")}
        isOpen={() => { return isFlyoutOpen && !allSlotsDisabled }}
        onClick={() => { if (!allSlotsDisabled) setIsFlyoutOpen(!isFlyoutOpen) }}
        shift={true}
        position="top-left">
        <div className={`${isMinimalTheme() && isScreenNarrow ? "flex-column" : "flex-row"} flexwrap`}>
          <span className="flex-row">
            {!isMinimalTheme() && <DiskImageChooser {...props} />}
            <DiskDrive key={0} index={0} renderCount={props.renderCount}
              setShowFileOpenDialog={props.setShowFileOpenDialog} updateDisplay={props.updateDisplay} />
            <DiskDrive key={1} index={1} renderCount={props.renderCount}
              setShowFileOpenDialog={props.setShowFileOpenDialog} updateDisplay={props.updateDisplay} />
            {(isMinimalTheme() && isScreenNarrow) && <VeraSdIcon renderCount={props.renderCount} />}
            {(isMinimalTheme() && isScreenNarrow) && <ImageWriter />}
          </span>
          <span className="flex-row">
            <DiskDrive key={2} index={2} renderCount={props.renderCount}
              setShowFileOpenDialog={props.setShowFileOpenDialog} updateDisplay={props.updateDisplay} />
            <DiskDrive key={3} index={3} renderCount={props.renderCount}
              setShowFileOpenDialog={props.setShowFileOpenDialog} updateDisplay={props.updateDisplay} />
            {(!isMinimalTheme() || !isScreenNarrow) && <VeraSdIcon renderCount={props.renderCount} />}
            {(!isMinimalTheme() || !isScreenNarrow) && <ImageWriter />}
          </span>
        </div>
      </Flyout>
    </span>
  )
}

export default DiskInterface